import { Box, SkeletonGroup, SkeletonRectangle, Tag, Text, Tooltip } from '@wix/design-system';
import { CoreDomainSearchBackend } from '@wix/core-domain-search';
import { CoreDomainSearchStore, DomainSearchSuiteModule } from '@wix/domains-search-suite';
import { GiftSmall } from '@wix/wix-ui-icons-common/lazy';
import clsx from 'clsx';
import React from 'react';
import { DomainSearchGateModule } from '../../module/domainSearchGate.module';
import { GateTldChipsDataHooks } from './dataHooks';
import s from './gateTldChips.scss';
import NoVoucherHarmonyIcon from '../../assets/images/NoVoucherHarmonyIcon.svg';
import { TLDS_LIMIT, useTldPress } from '../../hooks/useTldPress';
const VOUCHER_TOOLTIP_KEY = 'domains_search_gate.gate_revamp.tld_chips.voucher.tooltip';
const NO_VOUCHER_TOOLTIP_KEY = 'domains_search_gate.revamp_2.non_voucher.tld_filter.tooltip';
const FREE_SITE_TOOLTIP_KEY = 'domain_search_suite.domain_gate.tld_section.badge.free_with_plan';
const SKELETON_CHIP_WIDTH = '80px';
const SKELETON_CHIP_HEIGHT = '30px';
const ChipsRowSkeleton = () => /*#__PURE__*/React.createElement(Box, {
  dataHook: GateTldChipsDataHooks.skeleton,
  "aria-hidden": true,
  gap: "6px",
  align: "center",
  verticalAlign: "middle",
  flexWrap: "wrap",
  width: "100%"
}, Array.from({
  length: TLDS_LIMIT
}, (_, index) => /*#__PURE__*/React.createElement(SkeletonGroup, {
  key: index,
  skin: "light",
  className: s.skeletonChipGroup
}, /*#__PURE__*/React.createElement(SkeletonRectangle, {
  className: s.skeletonChip,
  width: SKELETON_CHIP_WIDTH,
  height: SKELETON_CHIP_HEIGHT
}))));
export const GateTldChips = () => {
  const _DomainSearchGateModu = DomainSearchGateModule.internals.useTranslations(),
    t = _DomainSearchGateModu.t;
  const _DomainSearchSuiteMod = DomainSearchSuiteModule.internals.useTranslations(),
    tSuite = _DomainSearchSuiteMod.t;
  const selectedTlds = CoreDomainSearchStore.useTlds();
  const _CoreDomainSearchBack = CoreDomainSearchBackend.hooks.useGetTldSelectorData({
      limit: TLDS_LIMIT
    }, true, {
      cacheTime: Infinity,
      staleTime: Infinity,
      useErrorBoundary: false,
      suspense: false,
      retry: false
    }),
    data = _CoreDomainSearchBack.data,
    isLoading = _CoreDomainSearchBack.isLoading;
  const availableTlds = (data == null ? void 0 : data.tldSelectorData) ?? [];
  const press = useTldPress('searchbar_tld');
  const handleClick = (tld, position) => {
    press(tld, {
      position,
      numOfTlds: availableTlds.length
    });
  };
  if (isLoading) {
    return /*#__PURE__*/React.createElement(ChipsRowSkeleton, null);
  }
  if (!availableTlds.length) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Box, {
    dataHook: GateTldChipsDataHooks.row,
    gap: "6px",
    align: "center",
    verticalAlign: "middle",
    flexWrap: "wrap",
    width: "100%"
  }, availableTlds.map((_ref, index) => {
    let tld = _ref.tld,
      isVoucherSupported = _ref.isVoucherSupported,
      hasValidVouchers = _ref.hasValidVouchers,
      isPremiumSite = _ref.isPremiumSite;
    const promotesFreeYear = !isPremiumSite || hasValidVouchers;
    const chip = /*#__PURE__*/React.createElement(Tag, {
      id: tld,
      skin: "lightOutlined",
      removable: false,
      className: clsx(s.tldChip, {
        [s.tldChipSelected]: selectedTlds.includes(tld)
      }),
      onClick: () => handleClick(tld, index + 1),
      dataHook: `${GateTldChipsDataHooks.option}-${tld}`
    }, /*#__PURE__*/React.createElement(Box, {
      gap: "6px",
      direction: "horizontal",
      verticalAlign: "middle"
    }, /*#__PURE__*/React.createElement(Text, {
      size: "small"
    }, ".", tld), isVoucherSupported && promotesFreeYear ? /*#__PURE__*/React.createElement(GiftSmall, null) : isVoucherSupported ? /*#__PURE__*/React.createElement(NoVoucherHarmonyIcon, null) : null));
    if (!isVoucherSupported) {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: tld
      }, chip);
    }
    return /*#__PURE__*/React.createElement(Tooltip, {
      key: tld,
      size: "medium",
      placement: "bottom",
      textAlign: "center",
      maxWidth: "202px",
      content: isPremiumSite ? t(hasValidVouchers ? VOUCHER_TOOLTIP_KEY : NO_VOUCHER_TOOLTIP_KEY, {
        tld: `.${tld}`
      }) : tSuite(FREE_SITE_TOOLTIP_KEY)
    }, chip);
  }));
};
//# sourceMappingURL=GateTldChips.js.map